import { useCallback, useEffect, useRef, useState } from "react";

export type AsyncStatus = "idle" | "loading" | "success" | "error";

interface AsyncState<T> {
  status: AsyncStatus;
  data: T | null;
  error: unknown | null;
}

export interface ExecuteOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: unknown) => void;
}

export function useAsyncAction<P, R>(
  asyncFn: (payload: P, signal?: AbortSignal) => Promise<R>,
) {
  const [state, setState] = useState<AsyncState<R>>({
    status: "idle",
    data: null,
    error: null,
  });

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const abortRefValue = abortRef.current;
    return () => {
      abortRefValue?.abort();
    };
  }, []);

  const execute = useCallback(
    async (payload: P, options?: ExecuteOptions<R>) => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setState({ status: "loading", data: null, error: null });

      try {
        const result = await asyncFn(payload, abortRef.current.signal);

        setState({ status: "success", data: result, error: null });
        options?.onSuccess?.(result);

        return result;
      } catch (error) {
        if ((error as any)?.name === "AbortError") return;

        setState({ status: "error", data: null, error });

        options?.onError?.(error);

        throw error;
      }
    },
    [asyncFn],
  );

  const reset = useCallback(() => {
    setState({ status: "idle", data: null, error: null });
  }, []);

  return {
    execute,
    reset,
    status: state.status,
    data: state.data,
    error: state.error,
    isIdle: state.status === "idle",
    isLoading: state.status === "loading",
    isSuccess: state.status === "success",
    isError: state.status === "error",
  };
}
